<script setup lang="ts">
import { Ref, computed, ref, watch } from 'vue';
import { FullColorObject } from '@/composables/color';

type SliderDirection = 'horizontal' | 'vertical';

const props = withDefaults(
    defineProps<{
        colors: FullColorObject;
        direction?: SliderDirection;
    }>(),
    { direction: 'horizontal' }
);

const emit = defineEmits<{
    (event: 'change', value: Partial<FullColorObject>): void;
}>();

const containerRef: Ref<HTMLDivElement | null> = ref(null);
const oldHue = ref(0);
const pullDirection: Ref<'right' | 'left' | ''> = ref('');

const directionClass = computed(() => {
    const classes: Record<SliderDirection, string> = {
        horizontal: 'c-hue--horizontal',
        vertical: 'c-hue--vertical'
    };

    return classes[props.direction];
});

const pointerTop = computed(() => {
    if (props.direction == 'horizontal') return 0;

    if (props.colors.hsl.h === 0 && pullDirection.value === 'right') return '100%';

    return -((props.colors.hsl.h * 100) / 360) + 100 + '%';
});

const pointerLeft = computed(() => {
    if (props.direction == 'vertical') return 0;

    if (props.colors.hsl.h === 0 && pullDirection.value === 'right') return '100%';

    return (props.colors.hsl.h * 100) / 360 + '%';
});

watch(
    () => props.colors.hsv,
    () => {
        const { h } = props.colors.hsv;
        if (h !== 0 && h - oldHue.value > 0) pullDirection.value = 'right';
        if (h !== 0 && h - oldHue.value < 0) pullDirection.value = 'left';
        oldHue.value = h;
    },
    { deep: true }
);

const handleChange = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();

    const container = containerRef.value;

    // for some edge cases, container may not exist
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const xOffset = container.getBoundingClientRect().left + window.scrollX;
    const yOffset = container.getBoundingClientRect().top + window.scrollY;
    const pageX = (e as MouseEvent).pageX || ((e as TouchEvent).touches ? (e as TouchEvent).touches[0].pageX : 0);
    const pageY = (e as MouseEvent).pageY || ((e as TouchEvent).touches ? (e as TouchEvent).touches[0].pageY : 0);
    const left = pageX - xOffset;
    const top = pageY - yOffset;

    let hue;
    let percent;

    if (props.direction === 'vertical') {
        if (top < 0) {
            hue = 360;
        } else if (top > containerHeight) {
            hue = 0;
        } else {
            percent = -((top * 100) / containerHeight) + 100;
            hue = (360 * percent) / 100;
        }
    } else {
        if (left < 0) {
            hue = 0;
        } else if (left > containerWidth) {
            hue = 360;
        } else {
            percent = (left * 100) / containerWidth;
            hue = (360 * percent) / 100;
        }
    }

    if (props.colors.hsl.h !== hue) {
        emit('change', {
            hsl: {
                ...props.colors.hsl,
                h: hue
            }
        });
    }
};

const handleMouseUp = () => {
    unbindEventListeners();
};

const handleMouseDown = () => {
    window.addEventListener('mousemove', handleChange);
    window.addEventListener('mouseup', handleMouseUp);
};

const unbindEventListeners = () => {
    window.removeEventListener('mousemove', handleChange);
    window.removeEventListener('mouseup', handleMouseUp);
};
</script>

<template>
    <div :class="['c-hue', directionClass]">
        <div
            ref="containerRef"
            class="c-hue-container"
            role="slider"
            :aria-valuenow="colors.hsl.h"
            aria-valuemin="0"
            aria-valuemax="360"
            @click="handleChange"
            @mousedown="handleMouseDown"
            @touchmove="handleChange"
            @touchstart="handleChange"
        >
            <div class="c-hue-pointer" :style="{ top: pointerTop, left: pointerLeft }" role="presentation">
                <div class="c-hue-picker" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.c-hue {
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    border-radius: 50rem;
    &--horizontal {
        background: linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%);
    }
    &--vertical {
        background: linear-gradient(to top, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%);
    }
    .c-hue-container {
        position: relative;
        height: 100%;
        cursor: pointer;
        margin-right: 10px;
        margin-left: 5px;
        .c-hue-pointer {
            position: absolute;
            z-index: 2;
        }
        .c-hue-picker {
            cursor: grab;
            width: 1.2rem;
            height: 1.2rem;
            border-radius: 50%;
            transform: translate(-6px, -2px);
            background: #fff;
            border: 3px solid blue;
            box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
        }
    }
}
</style>
